import {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  useRef,
  useState,
} from "react"

import { CheckIcon, CircleAlertIcon, LoaderCircle } from "lucide-react"
import { type FieldValues, type Path, useFormContext } from "react-hook-form"

import { Button } from "@/components/ui/button"
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { cn } from "@/lib/utils"

export type FormFileProps<T extends FieldValues = FieldValues> =
  ComponentPropsWithoutRef<"input"> & {
    name: Path<T>
    initialFileName?: string
    status: "idle" | "pending" | "success" | "error"
  }

function ButtonContents({
  status = "idle",
}: {
  status: FormFileProps["status"]
}) {
  if (status === "pending")
    return (
      <>
        <LoaderCircle className="animate-spin" />
        <span>Uploading...</span>
      </>
    )
  if (status === "success")
    return (
      <>
        <CheckIcon />
        <span>Uploaded</span>
      </>
    )
  if (status === "error")
    return (
      <>
        <CircleAlertIcon className="text-destructive" />
        <span className="text-destructive">Not Uploaded</span>
      </>
    )
  return <span>Upload File</span>
}

export function FormFile<T extends FieldValues = FieldValues>({
  name,
  className: propClassName,
  initialFileName,
  status = "idle",
  ...otherProps
}: FormFileProps<T>) {
  const form = useFormContext<T>()
  const [filename, setFilename] = useState<string | undefined>(initialFileName)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClick = () => inputRef.current?.click()

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem
          className={cn(
            "flex w-full flex-col items-start gap-1 sm:items-end",
            propClassName,
          )}
        >
          <>
            <Button
              type="button"
              variant="outline"
              className={cn(
                "text-primary hover:text-primary w-full shrink-0 justify-center gap-2 rounded-lg px-3 text-sm font-medium sm:w-36",
              )}
              onClick={handleClick}
            >
              <ButtonContents status={status} />
            </Button>
            <FormControl>
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                {...otherProps}
                disabled={otherProps.disabled || field.disabled}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0]
                  field.onChange(file)
                  setFilename(file?.name || undefined)
                  otherProps.onChange?.(e)
                }}
              />
            </FormControl>
          </>
          {/* File name below the button overflows to the left if necessary
            in order to avoid having the button grow in size with large
            filenames.
          */}
          {filename && (
            <span className="direction-rtl max-w-full overflow-hidden text-sm text-nowrap text-ellipsis sm:max-w-72">
              {filename}
            </span>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
